import React, { createRef, useEffect, useRef, useState } from 'react';
import { Button, CardBody, CardHeader } from 'reactstrap';
import { Row, Col, FormGroup } from 'reactstrap';
import { CurrencyInput, Field, Form, Input } from '@availity/form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import CroppingToolPopup from '../../../../../components/CroppingToolPopup/CroppingToolPopup';
import './PublicProfile.scss';
import * as yup from 'yup';
import { useStateSelector } from '../../../../../store/selectors';
import createNotification from '../../../../../utils/createNotification';
import { Multiselect } from 'react-widgets/cjs';
import { ArrowDown } from '../../../../../components/arrow-down/arrow-down';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import { HistoricalGrowthMap } from './HistoricalGrowthMap/HistoricalGrowthMap';
import Dropzone, { DropzoneRef } from 'react-dropzone';
import uploadArrow from '../../../../../assets/images/uploadArrow.png';
import { GeographyMarketList } from '../../../../../slices/marketplace/buyer/models/geography-market';
import { MarketplaceBuyerProfile } from '../../../../../slices/marketplace/buyer/models/buyer-profile';
import ConfirmationModal from '../../../../../components/Modal/ConfirmationModal';
import { GetBuyer } from '../../../../../slices/marketplace/buyer/thunks/get-buyer.thunk';
import { useDispatch } from 'react-redux';
import { BuyerUploads } from '../BuyerUploads/BuyerUploads';
import { MarketplaceBuyerUploadAccessLevel } from '../../../../../slices/marketplace/buyer/models/buyer-upload';
import { NdaTermPreferences } from './NdaTermPreferences/NdaTermPreferences';
import { getAllStatesFlat } from '../../../../../utils/locationsHelper';
import { useServiceFocuses } from '../../../../../custom-hooks/useServiceFocuses';
import { mapServiceFocusesToOptions } from '../../../../UserPages/Login/Modals/PracticeRegistrationForm/utils';

interface StatesByRegion {
    region: string;
    states: string[];
}

export interface PublicProfileProps {
    setIsEditMode: (isEditMode: boolean) => void;
}

export const PublicProfile = (props: PublicProfileProps) => {
    const [isUploadImagePopupVisible, setIsUploadImagePopupVisible] =
        useState(false);
    const dispatch = useDispatch() as any;
    const serviceFocuses = useServiceFocuses();
    const profile = useStateSelector(
        (state) => state.buyersState.targetBuyer.profile
    );
    const [isSaving, setIsSaving] = useState(false);
    const formRef = useRef(null);
    const FlexibleKey = 'Flexible';
    const axios = useStateSelector((state) => state.core.axios);
    const [allRegions, setAllRegions] = useState<string[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>(
        profile?.stateAcquisitionRegion ?? []
    );
    const [statesByRegions, setStatesByRegions] = useState<StatesByRegion[]>(
        []
    );
    const [selectedGeography, setSelectedGeography] = useState<string[]>(
        profile?.geographyMarket ?? []
    );
    const [allStates, setAllStates] = useState<string[]>([FlexibleKey]);
    const [logoData, setLogoData] = useState<FormData>(null);
    const [previewLogo, setPreviewLogo] = useState<string | null>(null);
    const [growthMapData, setGrowthMapData] = useState<FormData>(null);
    const [selectedLogoFile, setSelectedLogoFile] = useState<File>(null);
    const [shouldRemoveGrowthMapData, setShouldRemoveGrowthMapData] =
        useState<boolean>(false);
    const [shouldRemoveLogoData, setShouldRemoveLogoData] =
        useState<boolean>(false);
    const [
        isConfirmationLogoRemovalVisible,
        setIsConfirmationLogoRemovalVisible,
    ] = useState<boolean>(false);
    const [isRegionsListOpened, setIsRegionsListOpened] = useState(false);
    const openRegionsMultiselect = (regions: string[]) => {
        if (regions?.length >= allRegions.length) {
            setIsRegionsListOpened(false);
            window.focus();
        } else {
            setIsRegionsListOpened(true);
        }
    };
    const [isStatesListOpened, setIsStatesListOpened] = useState(false);
    const openStatesMultiselect = (states: string[]) => {
        if (states.length >= allStates.length) {
            setIsStatesListOpened(false);
            window.focus();
        } else {
            setIsStatesListOpened(true);
        }
    };
    const [isGeographyListOpened, setIsGeographyListOpened] = useState(false);
    const openGeographyMultiselect = (geography: string[]) => {
        if (geography?.length > GeographyMarketList.length) {
            setIsGeographyListOpened(false);
            window.focus();
        } else {
            setIsGeographyListOpened(true);
        }
    };
    const [targetPracticeSizeRevenueMin, setTargetPracticeSizeRevenueMin] =
        useState(profile?.targetPracticeSizeRevenueMin ?? 0);
    const [targetPracticeSizeRevenueMax, setTargetPracticeSizeRevenueMax] =
        useState(profile?.targetPracticeSizeRevenueMax ?? 0);
    const [
        targetPracticeSizeRevenueSweetspot,
        setTargetPracticeSizeRevenueSweetspot,
    ] = useState(profile?.targetPracticeSizeRevenueSweetspot ?? 0);

    const getAvailableStates = (regions: string[]) => {
        if (!regions?.length) return [];
        if (
            regions.includes(FlexibleKey) ||
            regions.length === allRegions.length - 1
        )
            return allStates;

        return [
            FlexibleKey,
            ...statesByRegions
                .filter((sr) => regions.includes(sr.region))
                .flatMap((regionWithStates) => regionWithStates.states)
                .filter(
                    (currentValue, index, arr) =>
                        arr.indexOf(currentValue) === index
                )
                .sort(),
        ];
    };

    const [practiceFocusOptions] = useState(
        Object.entries(mapServiceFocusesToOptions(serviceFocuses)).map(([id, label]) => ({
            id: Number(id),
            option: label,
        }))
    );
    const [isPracticeFocusesListOpened, setIsPracticeFocusesListOpened] =
        useState(false);
    const openPracticeFocusesMultiselect = (focuses: number[]) => {
        if (
            focuses.length >=
            Object.keys(mapServiceFocusesToOptions(serviceFocuses)).length
        ) {
            setIsPracticeFocusesListOpened(false);
            window.focus();
        } else {
            setIsPracticeFocusesListOpened(true);
        }
    };

    const [displayStates, setDisplayStates] = useState<string[]>(
        getAvailableStates(profile?.stateAcquisitionRegion ?? [])
    );
    const [selectedStates, setSelectedStates] = useState<string[]>(
        profile?.stateAcquisitionState ?? []
    );
    const [selectedFocuses, setSelectedFocuses] = useState<number[]>(
        profile?.practiceFocuses ?? []
    );
    const dropzoneRef = createRef<DropzoneRef>();
    const allowedImageExtensions = ['.jpg', '.jpeg', '.png', '.gif'];

    const openUploadDialog = () => {
        if (dropzoneRef.current) {
            dropzoneRef.current.open();
        }
    };
    const renderLogoUpload = () => (
        <div className="image-container">
            <div className="upload-image-container">
                <div className="label-container">
                    <label>Upload Your Company Logo</label>
                </div>
                <div className="upload-image-wrapper">
                    <div>
                        <div className="upload-box">
                            <Dropzone
                                noClick
                                noKeyboard
                                ref={dropzoneRef}
                                onDrop={(acceptedFiles: File[]) => {
                                    setSelectedLogoFile(acceptedFiles[0]);
                                    setIsUploadImagePopupVisible(true);
                                }}
                                accept={{
                                    'image/jpeg': allowedImageExtensions,
                                }}>
                                {({ getRootProps, getInputProps }) => {
                                    return (
                                        <div>
                                            <div
                                                {...getRootProps({
                                                    className: 'dropzone',
                                                })}>
                                                <input
                                                    {...getInputProps()}
                                                    accept={allowedImageExtensions.join(
                                                        ','
                                                    )}
                                                />
                                                <div>
                                                    <img src={uploadArrow} />
                                                </div>
                                                <Button
                                                    className="btn btn-primary upload-btn"
                                                    color="primary"
                                                    onClick={() =>
                                                        openUploadDialog()
                                                    }>
                                                    Upload
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                }}
                            </Dropzone>
                        </div>
                    </div>

                    <div className="preview-image-container">
                        {shouldRemoveLogoData ? (
                            <div className="empty-logo-area">
                                There is no logo uploaded yet
                            </div>
                        ) : previewLogo ? (
                            <img src={previewLogo} alt="Logo Preview" />
                        ) : profile.logoUrl ? (
                            <img src={profile.logoUrl} alt="Current Logo" />
                        ) : (
                            <div className="empty-logo-area">
                                There is no logo uploaded yet
                            </div>
                        )}
                        {(profile.logoUrl || previewLogo) &&
                        !shouldRemoveLogoData ? (
                            <div className="remove-image-container">
                                <FontAwesomeIcon
                                    className="delete-button"
                                    onClick={() => {
                                        setIsConfirmationLogoRemovalVisible(
                                            true
                                        );
                                    }}
                                    icon={faTrashCan}
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderGrowthMapUpload = () => (
        <div className="growth-map-container">
            <HistoricalGrowthMap
                setShouldRemoveGrowthMapData={setShouldRemoveGrowthMapData}
                shouldRemoveGrowthMapData={shouldRemoveGrowthMapData}
                setGrowthMapData={setGrowthMapData}
                growthMapUrl={profile.growthMapUrl}></HistoricalGrowthMap>
        </div>
    );

    useEffect(() => {
        setDisplayStates(
            getAvailableStates(profile?.stateAcquisitionRegion ?? [])
        );
    }, [statesByRegions]);

    const getAllStates = (statesByRegionsParam: StatesByRegion[] = null) =>
        (statesByRegionsParam ?? statesByRegions)
            ?.flatMap((regionWithStates) => regionWithStates.states)
            .filter(
                (currentValue, index, arr) =>
                    arr.indexOf(currentValue) === index
            )
            .sort() ?? [];

    useEffect(() => {
        axios
            .get('api/location/StatesByRegion')
            .then((response: any) => {
                if (response && response.data && response.data.data) {
                    const allStates = getAllStatesFlat(response.data.data);
                    setAllStates([FlexibleKey, ...allStates]);

                    const regions: string[] = response.data.data.map(
                        (r: StatesByRegion) => r.region
                    );
                    setAllRegions([FlexibleKey, ...regions]);

                    setStatesByRegions(response.data.data);
                } else {
                    createNotification(
                        'An unexpected error occurred while loading Location Data.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while loading Location Data.',
                    'error'
                );
            });
    }, []);

    const renderFileUploads = () => (
        <>
            <CardHeader className={'buyer-uploads-header'}>
                <div className="header-wrapper">
                    <div>Public Documents</div>
                </div>
            </CardHeader>
            <BuyerUploads
                accessLevel={
                    MarketplaceBuyerUploadAccessLevel.Public
                }></BuyerUploads>
        </>
    );

    const updateProfile = async () => {
        return await dispatch(GetBuyer(null));
    };

    const changeLogo = (file: File, fileUrl: string) => {
        setPreviewLogo(fileUrl);
        setShouldRemoveLogoData(false);
        const formData = new FormData();
        formData.append('File', file);
        formData.append('Url', fileUrl);
        setLogoData(formData);
    };

    const saveProfile = async (profile: MarketplaceBuyerProfile) => {
        setIsSaving(true);
        let requests = [];
        if (shouldRemoveLogoData) {
            const logoRemovalRequest = axios.delete(
                `/api/Marketplace/MarketplaceBuyerProfiles/Logo`
            );
            requests.push(logoRemovalRequest);
        }
        if (logoData) {
            const logoRequest = axios.post(
                `/api/Marketplace/MarketplaceBuyerProfiles/UploadLogo`,
                logoData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            requests.push(logoRequest);
        }
        if (shouldRemoveGrowthMapData) {
            const mapRemovalRequest = axios.delete(
                `/api/Marketplace/MarketplaceBuyerProfiles/GrowthMap`
            );
            requests.push(mapRemovalRequest);
        }
        if (growthMapData) {
            const growthMapRequest = axios.post(
                `/api/Marketplace/MarketplaceBuyerProfiles/UploadGrowthMap`,
                growthMapData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            requests.push(growthMapRequest);
        }
        const profileRequest = axios.put(
            `/api/Marketplace/MarketplaceBuyerProfiles`,
            profile
        );
        requests.push(profileRequest);
        await Promise.allSettled(requests)
            .catch(() => {
                createNotification(
                    'Something went wrong during saving profile',
                    'error'
                );
            })
            .finally(() => {
                updateProfile().finally(() => {
                    setLogoData(null);
                    setGrowthMapData(null);
                    setShouldRemoveGrowthMapData(false);
                    setShouldRemoveLogoData(false);
                    setIsSaving(false);
                });
            });
    };

    const handleRegionsUpdate = (newRegionsParam: string[]) => {
        const becameEmpty =
            !!selectedRegions?.length && !newRegionsParam?.length;
        const becameFlexible =
            newRegionsParam.includes(FlexibleKey) &&
            !selectedRegions.includes(FlexibleKey);
        const allOptionsButFlexible =
            !newRegionsParam.includes(FlexibleKey) &&
            newRegionsParam.length === allRegions.length - 1;
        const becameUnFlexible =
            selectedRegions.length === 1 &&
            selectedRegions.includes(FlexibleKey) &&
            newRegionsParam.length > 1;
        const regionRemoved = selectedRegions.length > newRegionsParam.length;
        const newRegions =
            becameFlexible || allOptionsButFlexible
                ? [FlexibleKey]
                : becameUnFlexible
                ? newRegionsParam.filter((r) => r !== FlexibleKey)
                : newRegionsParam;

        openRegionsMultiselect(newRegions);
        setSelectedRegions(newRegions);
        formRef.current.setFieldValue('stateAcquisitionRegion', newRegions);

        const newAvailableStates = getAvailableStates(newRegions ?? []);
        setDisplayStates(newAvailableStates);

        if (becameEmpty) {
            setSelectedStates(newAvailableStates);
            formRef.current.setFieldValue(
                'stateAcquisitionState',
                newAvailableStates
            );
        } else if (becameUnFlexible || regionRemoved) {
            const newSelectedStates = selectedStates.filter((s) =>
                newAvailableStates.includes(s)
            );
            if (newSelectedStates !== selectedStates) {
                setSelectedStates(newSelectedStates);
                formRef.current.setFieldValue(
                    'stateAcquisitionState',
                    newSelectedStates
                );
            }
        }
    };

    const handleStatesUpdate = (newStatesParam: string[]) => {
        const becameFlexible =
            newStatesParam.includes(FlexibleKey) &&
            !selectedStates.includes(FlexibleKey);
        const allOptionsButFlexible =
            !newStatesParam.includes(FlexibleKey) &&
            newStatesParam.length === displayStates.length - 1;
        const becameUnFlexible =
            selectedStates.length === 1 &&
            selectedStates.includes(FlexibleKey) &&
            newStatesParam.length > 1;
        const newStates =
            becameFlexible || allOptionsButFlexible
                ? [FlexibleKey]
                : becameUnFlexible
                ? newStatesParam.filter((s) => s !== FlexibleKey)
                : newStatesParam;

        openStatesMultiselect(newStates);
        setSelectedStates(newStates);
        formRef.current.setFieldValue('stateAcquisitionState', newStates);
    };

    const getFteDvmsRange = () => {
        const fteDvms = [];
        for (let i = 1; i < 50.5; i += 0.5) {
            fteDvms.push({ id: i, name: i });
        }

        return fteDvms;
    };

    const handleGeographyUpdate = (newGeographyParams: string[]) => {
        const becameFlexible =
            newGeographyParams.includes(FlexibleKey) &&
            !selectedGeography.includes(FlexibleKey);

        const allOptionsButFlexible =
            !newGeographyParams.includes(FlexibleKey) &&
            newGeographyParams.length === GeographyMarketList.length;

        const becameUnFlexible =
            selectedGeography.length === 1 &&
            selectedGeography.includes(FlexibleKey) &&
            newGeographyParams.length > 1;

        const newGeography =
            becameFlexible || allOptionsButFlexible
                ? [FlexibleKey]
                : becameUnFlexible
                ? newGeographyParams.filter((s) => s !== FlexibleKey)
                : newGeographyParams;

        setSelectedGeography(newGeography);
        formRef.current.setFieldValue('geographyMarket', newGeography);
    };

    const renderForm = () => {
        return (
            <Form
                innerRef={formRef}
                validationSchema={yup.object().shape({
                    website: yup
                        .string()
                        .nullable()
                        .matches(
                            /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                            'Invalid URL format'
                        ),
                })}
                initialValues={{
                    website: profile?.website ?? '',
                    companyDescription: profile?.companyDescription ?? '',
                    stateAcquisitionRegion: profile?.stateAcquisitionRegion ?? [
                        '',
                    ],
                    stateAcquisitionState: profile?.stateAcquisitionState ?? [
                        '',
                    ],
                    geographyMarket: profile?.geographyMarket ?? [''],
                    targetPracticeSizeRevenueMin:
                        profile?.targetPracticeSizeRevenueMin ?? 0,
                    targetPracticeSizeRevenueMax:
                        profile?.targetPracticeSizeRevenueMax ?? 0,
                    targetPracticeSizeRevenueSweetspot:
                        profile?.targetPracticeSizeRevenueSweetspot ?? 0,
                    targetPracticeSizeFteMin:
                        profile?.targetPracticeSizeFteMin ?? 0,
                    targetPracticeSizeFteMax:
                        profile?.targetPracticeSizeFteMax ?? 0,
                    targetPracticeSizeFteSweetspot:
                        profile?.targetPracticeSizeFteSweetspot ?? 0,
                    logoUrl: profile?.logoUrl ?? '',
                    growthMapUrl: profile?.growthMapUrl ?? '',
                    nonDisclosurePeriod:
                        profile?.currentNdaTermPreferences?.nonDisclosurePeriod,
                    nonSolicitationDvmNamesVisible:
                        profile?.currentNdaTermPreferences
                            ?.nonSolicitationDvmNamesVisible,
                    nonSolicitationDvmNamesBlinded:
                        profile?.currentNdaTermPreferences
                            ?.nonSolicitationDvmNamesBlinded,
                    nonSolicitationStaffNamesVisible:
                        profile?.currentNdaTermPreferences
                            ?.nonSolicitationStaffNamesVisible,
                    nonSolicitationStaffNamesBlinded:
                        profile?.currentNdaTermPreferences
                            ?.nonSolicitationStaffNamesBlinded,
                    companyName: profile?.companyName,
                    practiceFocuses: profile?.practiceFocuses ?? [],
                }}
                onSubmit={(values) => {
                    const result = {
                        website: values.website,
                        companyDescription: values.companyDescription,
                        stateAcquisitionRegion: values.stateAcquisitionRegion,
                        stateAcquisitionState: values.stateAcquisitionState,
                        geographyMarket: values.geographyMarket,
                        targetPracticeSizeRevenueMin:
                            values.targetPracticeSizeRevenueMin,
                        targetPracticeSizeRevenueMax:
                            values.targetPracticeSizeRevenueMax,
                        targetPracticeSizeRevenueSweetspot:
                            values.targetPracticeSizeRevenueSweetspot,
                        targetPracticeSizeFteMin:
                            values.targetPracticeSizeFteMin,
                        targetPracticeSizeFteMax:
                            values.targetPracticeSizeFteMax,
                        targetPracticeSizeFteSweetspot:
                            values.targetPracticeSizeFteSweetspot,
                        logoUrl: values.logoUrl,
                        growthMapUrl: values.growthMapUrl,
                        uploads: profile.uploads,
                        legalDocumentInputs: profile.legalDocumentInputs,
                        currentNdaTermPreferences: {
                            id: profile?.currentNdaTermPreferences?.id,
                            nonDisclosurePeriod: values.nonDisclosurePeriod,
                            nonSolicitationDvmNamesVisible:
                                values.nonSolicitationDvmNamesVisible,
                            nonSolicitationDvmNamesBlinded:
                                values.nonSolicitationDvmNamesBlinded,
                            nonSolicitationStaffNamesVisible:
                                values.nonSolicitationStaffNamesVisible,
                            nonSolicitationStaffNamesBlinded:
                                values.nonSolicitationStaffNamesBlinded,
                        },
                        practiceFocuses: values.practiceFocuses,
                        companyName: values.companyName,
                    } as MarketplaceBuyerProfile;
                    saveProfile(result);
                }}>
                {renderGeneralDescription()}
                {renderNdaTermPreferences()}
            </Form>
        );
    };

    const renderGeneralDescription = () => {
        return (
            <div className="general-description">
                <CardHeader className="public-profile-header mb-15">
                    Public Description
                </CardHeader>
                <CardBody className="description-form">
                    <Col>
                        <Row>
                            <Col md={4} className="pl-0">
                                <div className="combined-section">
                                    <FormGroup>
                                        <label>Company Name:</label>
                                        <Field
                                            disabled={isSaving}
                                            type="text"
                                            name="companyName"
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <label>Website:</label>
                                        <Field
                                            disabled={isSaving}
                                            type="url"
                                            name="website"
                                        />
                                    </FormGroup>
                                </div>
                            </Col>
                            <Col md={8} className="pl-0">
                                <FormGroup className="company-description-section">
                                    <label>Company Description:</label>
                                    <Input
                                        className="company-description"
                                        disabled={isSaving}
                                        type="textarea"
                                        name="companyDescription"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Row className="mb-0">
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>Acquisition Focus - Regions:</label>
                                </Row>
                                <Row>
                                    <div className={'multiselect'}>
                                        <Multiselect
                                            selectIcon={<ArrowDown />}
                                            data={allRegions}
                                            open={isRegionsListOpened}
                                            value={selectedRegions}
                                            onFocus={() => {
                                                openRegionsMultiselect(
                                                    selectedRegions
                                                );
                                            }}
                                            onBlur={() => {
                                                setIsRegionsListOpened(false);
                                            }}
                                            onChange={handleRegionsUpdate}
                                        />
                                    </div>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>Acquisition Focus - States:</label>
                                </Row>
                                <Row>
                                    <div className={'multiselect'}>
                                        <Multiselect
                                            disabled={!selectedRegions?.length}
                                            selectIcon={<ArrowDown />}
                                            data={displayStates}
                                            open={isStatesListOpened}
                                            value={selectedStates}
                                            onFocus={() => {
                                                openStatesMultiselect(
                                                    selectedStates
                                                );
                                            }}
                                            onBlur={() => {
                                                setIsStatesListOpened(false);
                                            }}
                                            onChange={handleStatesUpdate}
                                        />
                                    </div>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>Geography - Market:</label>
                                </Row>
                                <Row>
                                    <div className={'multiselect'}>
                                        <Multiselect
                                            disabled={isSaving}
                                            selectIcon={<ArrowDown />}
                                            data={[
                                                FlexibleKey,
                                                ...GeographyMarketList,
                                            ]}
                                            open={isGeographyListOpened}
                                            value={selectedGeography}
                                            onFocus={() =>
                                                openGeographyMultiselect(
                                                    selectedGeography
                                                )
                                            }
                                            onBlur={() =>
                                                setIsGeographyListOpened(false)
                                            }
                                            onChange={handleGeographyUpdate}
                                        />
                                    </div>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        {' '}
                        <Row className="mb-0">
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>
                                        Target Practice Size - Minimum Revenue:
                                    </label>
                                </Row>
                                <Row>
                                    <FormGroup>
                                        <CurrencyInput
                                            name="targetPracticeSizeRevenueMin"
                                            allowDecimals={false}
                                            allowNegativeValue={true}
                                            disabled={isSaving}
                                            prefix="$"
                                            className="input-with-icon min-width form-control currency-input"
                                            value={targetPracticeSizeRevenueMin.toString()}
                                            onValueChanged={(value) => {
                                                setTargetPracticeSizeRevenueMin(
                                                    value && value !== 'NaN'
                                                        ? Number(value)
                                                        : 0
                                                );
                                            }}
                                        />
                                    </FormGroup>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>
                                        Target Practice Size - Maximum Revenue:
                                    </label>
                                </Row>
                                <Row>
                                    <FormGroup>
                                        <CurrencyInput
                                            name="targetPracticeSizeRevenueMax"
                                            allowDecimals={false}
                                            allowNegativeValue={true}
                                            disabled={isSaving}
                                            prefix="$"
                                            className="input-with-icon min-width form-control currency-input"
                                            value={targetPracticeSizeRevenueMax.toString()}
                                            onValueChanged={(value) => {
                                                setTargetPracticeSizeRevenueMax(
                                                    value && value !== 'NaN'
                                                        ? Number(value)
                                                        : 0
                                                );
                                            }}
                                        />
                                    </FormGroup>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>
                                        Target Practice Size - Sweetspot
                                        Revenue:
                                    </label>
                                </Row>
                                <Row>
                                    <FormGroup>
                                        <CurrencyInput
                                            name="targetPracticeSizeRevenueSweetspot"
                                            allowDecimals={false}
                                            allowNegativeValue={true}
                                            disabled={isSaving}
                                            prefix="$"
                                            className="input-with-icon min-width form-control currency-input"
                                            value={targetPracticeSizeRevenueSweetspot.toString()}
                                            onValueChanged={(value) => {
                                                setTargetPracticeSizeRevenueSweetspot(
                                                    value && value !== 'NaN'
                                                        ? Number(value)
                                                        : 0
                                                );
                                            }}
                                        />
                                    </FormGroup>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Row className="mb-0">
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>
                                        Target Practice Size - Minimum FTE DVMs:
                                    </label>
                                </Row>
                                <Row>
                                    <FormGroup>
                                        <Input
                                            type="select"
                                            disabled={isSaving}
                                            name="targetPracticeSizeFteMin">
                                            {[
                                                { id: '', name: '' },
                                                ...getFteDvmsRange(),
                                            ].map((item) => (
                                                <option
                                                    value={item.id}
                                                    key={
                                                        'numberOfFteDvms' +
                                                        item.id
                                                    }>
                                                    {item.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>
                                        Target Practice Size - Maximum FTE DVMs:
                                    </label>
                                </Row>
                                <Row>
                                    <FormGroup>
                                        <Input
                                            type="select"
                                            disabled={isSaving}
                                            name="targetPracticeSizeFteMax">
                                            {[
                                                { id: '', name: '' },
                                                ...getFteDvmsRange(),
                                            ].map((item) => (
                                                <option
                                                    value={item.id}
                                                    key={
                                                        'numberOfFteDvms' +
                                                        item.id
                                                    }>
                                                    {item.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Row>
                            </Col>
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>
                                        Target Practice Size - Sweetspot FTE
                                        DVMs:
                                    </label>
                                </Row>
                                <Row>
                                    <FormGroup>
                                        <Input
                                            type="select"
                                            disabled={isSaving}
                                            name="targetPracticeSizeFteSweetspot">
                                            {[
                                                { id: '', name: '' },
                                                ...getFteDvmsRange(),
                                            ].map((item) => (
                                                <option
                                                    value={item.id}
                                                    key={
                                                        'numberOfFteDvms' +
                                                        item.id
                                                    }>
                                                    {item.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Row>
                            </Col>
                        </Row>
                    </Col>

                    <Col>
                        <Row className="mb-0">
                            <Col md={4}>
                                <Row className="mb-0">
                                    <label>Service Focus:</label>
                                </Row>
                                <Row>
                                    <div className={'multiselect'}>
                                        <Multiselect
                                            selectIcon={<ArrowDown />}
                                            data={practiceFocusOptions}
                                            open={isPracticeFocusesListOpened}
                                            value={practiceFocusOptions.filter(
                                                (o: any) =>
                                                    selectedFocuses.includes(
                                                        o.id
                                                    )
                                            )}
                                            dataKey="id"
                                            textField="option"
                                            onFocus={() => {
                                                openPracticeFocusesMultiselect(
                                                    selectedFocuses
                                                );
                                            }}
                                            onBlur={() => {
                                                setIsPracticeFocusesListOpened(
                                                    false
                                                );
                                            }}
                                            onChange={(value: any) => {
                                                const ids = value.map((x: any) => {
                                                    return x.id;
                                                });
                                                setSelectedFocuses(ids);
                                                formRef.current.setFieldValue(
                                                    'practiceFocuses',
                                                    ids
                                                );
                                            }}
                                        />
                                    </div>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </CardBody>
            </div>
        );
    };

    const renderNdaTermPreferences = () => {
        return (
            <div className="nda-terms-preferences">
                <CardHeader className="public-profile-header mb-15">
                    Data NDA Terms Preferences
                </CardHeader>
                <NdaTermPreferences
                    isFullScreen={false}
                    isReadonly={false}
                    isSaving={isSaving}></NdaTermPreferences>
            </div>
        );
    };

    return (
        <div className="public-profile">
            <CardHeader className="public-profile-header space-between">
                <div>{'Public Profile'}</div>
                <div className="buttons-wrapper">
                    <ButtonLoader
                        buttonText={`Save`}
                        loaderButtonText={''}
                        disabled={false}
                        isLoading={isSaving}
                        onClick={() => {
                            formRef.current.handleSubmit();
                        }}
                        className={
                            'btn event-btn btn-primary upload-btn loader-submit'
                        }></ButtonLoader>
                    <Button
                        className="btn btn-primary"
                        onClick={() => {
                            props.setIsEditMode(false);
                        }}>
                        Back
                    </Button>
                </div>
            </CardHeader>
            <CardBody className="card-body-custom">
                <Row>
                    <Col md={7}>{renderLogoUpload()}</Col>
                    <Col md={5}>{renderGrowthMapUpload()}</Col>
                </Row>
                <Row>
                    {' '}
                    <Col className="p-0" md={12}>
                        {renderForm()}
                    </Col>
                </Row>
                <Row>
                    {' '}
                    <Col className="p-0" md={12}>
                        {renderFileUploads()}
                    </Col>
                </Row>
            </CardBody>
            {isUploadImagePopupVisible && (
                <CroppingToolPopup
                    initialAspect={13 / 3}
                    header="Upload Logo"
                    isVisible={isUploadImagePopupVisible}
                    preSelectedFile={selectedLogoFile}
                    close={() => setIsUploadImagePopupVisible(false)}
                    apply={(croppedFile, croppedImage) => {
                        changeLogo(croppedFile, croppedImage);
                    }}
                />
            )}
            {isConfirmationLogoRemovalVisible && (
                <ConfirmationModal
                    isVisible={isConfirmationLogoRemovalVisible}
                    setIsVisible={() => {
                        setIsConfirmationLogoRemovalVisible(false);
                    }}
                    header={'Are you sure?'}
                    confirmationText={
                        'Are you sure you want to delete profile logo?'
                    }
                    onConfirm={() => {
                        setShouldRemoveLogoData(true);
                        setLogoData(null);
                        setPreviewLogo(null);
                    }}
                    params={undefined}
                    cancelButtonText={'No, take me back'}
                    nextButtonText={`Yes, I'm sure`}></ConfirmationModal>
            )}
        </div>
    );
};
